import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { UserProfile } from '../App';
import { AiCompanionResponse, callAiCompanion } from '../utils/aiCompanion';

type AiTab = 'daily_suggestion' | 'message_helper' | 'pet_voice';

interface Props { user: UserProfile; compact?: boolean; defaultTab?: AiTab; }

const TABS: { id: AiTab; label: string; title: string }[] = [
  { id: 'daily_suggestion', label: 'Öneri', title: 'Bugün ne iyi gelir?' },
  { id: 'message_helper', label: 'Mesaj', title: 'Mesajı yumuşat' },
  { id: 'pet_voice', label: 'Minik', title: 'Minik ne der?' },
];

const MESSAGE_STYLES = ['Tatlı', 'Özür', 'Destek', 'Kısa ve net'];

function userFacingError(err: unknown) {
  const code = typeof err === 'object' && err && 'code' in err ? String((err as { code?: string }).code) : '';
  const message = err instanceof Error ? err.message : String(err);
  const joined = `${code} ${message}`;
  if (joined.includes('resource-exhausted')) return 'Bugünkü AI hakkı doldu.';
  if (joined.includes('invalid-argument')) return 'Önce kısa bir mesaj yaz.';
  if (joined.includes('unauthenticated')) return 'AI için önce giriş yapmak gerekiyor.';
  return 'AI şu an cevap veremedi.';
}

export default function AiCompanionPanel({ user, compact = false, defaultTab = 'daily_suggestion' }: Props) {
  const [tab, setTab] = useState<AiTab>(defaultTab);
  const [style, setStyle] = useState(MESSAGE_STYLES[0]);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<AiCompanionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const active = TABS.find(item => item.id === tab) || TABS[0];
  const cta = tab === 'message_helper' ? 'Yumuşat' : tab === 'pet_voice' ? 'Minik’i konuştur' : 'Öneri al';

  const run = async () => {
    setError('');
    if (tab === 'message_helper' && !message.trim()) {
      setError('Önce kısa bir mesaj yaz.');
      return;
    }
    setLoading(true);
    try {
      const response = await callAiCompanion({
        mode: tab,
        message,
        style,
      });
      setResult(response);
    } catch (err) {
      console.warn('[AI] callable failed', err);
      setError(userFacingError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={`ai-panel ${compact ? 'compact' : ''}`}>
      <div className="ai-panel-head">
        <div>
          <small>AI bağ asistanı</small>
          <h2>{active.title}</h2>
        </div>
        <span>{result?.usage ? `${result.usage.remainingUser}/5` : 'limitli'}</span>
      </div>

      <div className="ai-tabs">
        {TABS.map(item => (
          <button
            key={item.id}
            type="button"
            className={tab === item.id ? 'active' : ''}
            onClick={() => { setTab(item.id); setError(''); setResult(null); }}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'message_helper' && (
        <div className="ai-message-box">
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            maxLength={500}
            placeholder={`${user.displayName}, buraya göndermek istediğin mesajı yaz`}
          />
          <div className="ai-style-row">
            {MESSAGE_STYLES.map(item => (
              <button key={item} type="button" className={style === item ? 'active' : ''} onClick={() => setStyle(item)}>
                {item}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab !== 'message_helper' && (
        <p className="ai-panel-sub">
          {tab === 'daily_suggestion'
            ? 'Mood, döngü, Minik ve günlük ritme göre tek küçük aksiyon.'
            : 'Minik bugüne göre kısa, kişisel bir cümle söyler.'}
        </p>
      )}

      <motion.button type="button" whileTap={{ scale: 0.97 }} className="ai-run-btn" onClick={run} disabled={loading}>
        {loading ? 'Düşünüyor...' : cta}
      </motion.button>

      {(result || error) && (
        <div className={`ai-result ${error ? 'error' : ''}`}>
          {error || result?.text}
          {result && (
            <small>
              {result.cached ? 'Cache' : result.source === 'fallback' ? 'Yerel cevap' : 'AI'}
              {result.usage ? ` · bugün ${result.usage.remainingCouple} çift hakkı kaldı` : ''}
            </small>
          )}
        </div>
      )}
    </section>
  );
}
